import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import saasApi from "../utils/saasApi";
import { AlertVisualProvider } from "./AlertVisualContext";
import { alertVisualStorageKey } from "../components/alerts/alertVisualPreferences";

const STORE_KEY = "absolluty.platform.store";
const RANGE_KEY = "absolluty.platform.dateRange";
const MOCK_KEY = "absolluty.platform.showMockData";

const toDateInput = (date) => date.toISOString().slice(0, 10);
const defaultDateRange = () => ({
  from: toDateInput(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
  to: toDateInput(new Date()),
  preset: "7d",
});

const PlatformContext = createContext(null);

const readJson = (key, fallback) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
};

export const PlatformProvider = ({ children }) => {
  const [stores, setStores] = useState([]);
  const [storeStatus, setStoreStatus] = useState("loading");
  const [selectedStoreId, setSelectedStoreId] = useState(() => localStorage.getItem(STORE_KEY) || "");
  const [dateRange, setDateRangeState] = useState(() => readJson(RANGE_KEY, defaultDateRange()));
  const [dataMode, setDataMode] = useState({ mode: "real", label: "Real data", source: "Absolluty API" });
  const [showMockData, setShowMockDataState] = useState(() => localStorage.getItem(MOCK_KEY) === "1");

  useEffect(() => {
    let alive = true;
    setStoreStatus("loading");
    saasApi.getBranches()
      .then((rows) => {
        if (!alive) return;
        const normalized = rows.map((store) => ({
          id: String(store.id),
          name: store.name || store.display_name || `Store ${store.id}`,
          status: store.status || "active",
          region: store.city || store.region || store.address || "",
          cameraCount: Number(store.camera_count || store.cameras_count || 0),
        }));
        setStores(normalized);
        setStoreStatus("ready");
        if (!selectedStoreId && normalized[0]?.id) setSelectedStoreId(normalized[0].id);
      })
      .catch(() => {
        if (!alive) return;
        setStores([]);
        setStoreStatus("error");
      });
    return () => { alive = false; };
  }, [selectedStoreId]);

  useEffect(() => {
    if (selectedStoreId) localStorage.setItem(STORE_KEY, selectedStoreId);
  }, [selectedStoreId]);

  const setDateRange = useCallback((next) => {
    const value = typeof next === "function" ? next(dateRange) : next;
    setDateRangeState(value);
    try { localStorage.setItem(RANGE_KEY, JSON.stringify(value)); } catch { /* localStorage unavailable */ }
  }, [dateRange]);

  const setShowMockData = useCallback((next) => {
    const value = typeof next === "function" ? next(showMockData) : Boolean(next);
    setShowMockDataState(value);
    try { localStorage.setItem(MOCK_KEY, value ? "1" : "0"); } catch { /* localStorage unavailable */ }
  }, [showMockData]);

  const selectedStore = useMemo(
    () => stores.find((store) => String(store.id) === String(selectedStoreId)) || stores[0] || null,
    [stores, selectedStoreId]
  );

  const value = useMemo(() => ({
    stores,
    storeStatus,
    selectedStore,
    selectedStoreId,
    setSelectedStoreId,
    dateRange,
    setDateRange,
    dataMode,
    setDataMode,
    showMockData,
    setShowMockData,
  }), [stores, storeStatus, selectedStore, selectedStoreId, dateRange, setDateRange, dataMode, showMockData, setShowMockData]);

  return <PlatformContext.Provider value={value}><AlertVisualProvider key={alertVisualStorageKey()}>{children}</AlertVisualProvider></PlatformContext.Provider>;
};

export const usePlatform = () => {
  const context = useContext(PlatformContext);
  if (!context) throw new Error("usePlatform must be used inside PlatformProvider");
  return context;
};
